// Decisions for the device-capture dialog about how to treat Capture Agent replies.
// Kept free of network code so the rules can be unit tested on their own.

// Key the Capture Agent uses for "Local Files / Network Files" in every released build.
// Opening it never touches scanner hardware, so it is the safe device to fall back to.
export const FOLDER_DEVICE_KEY = "___Folder_Device___";

// Exact ErrorMessage the Capture Agent returns from GetNextImage when its queue is empty.
// It means different things depending on timing: a normal end of scan (fast), or the
// agent giving up after its own 60 second wait while the scan is still running (slow).
export const NO_IMAGES_MESSAGE = "There are no images to capture.";

// The agent replies in the Windows display language, so the same reply has one wording
// per language the agent ships (Resources.resx, Resources.fr-CA.resx, Resources.nl.resx).
export const NO_IMAGES_MESSAGES = [
  NO_IMAGES_MESSAGE,
  "Il n'y a pas d'images à capturer.",
  "Er zijn geen documenten om te scannen.",
];

// Part of the agent's reply when another scanner operation is still running.
export const AGENT_BUSY_FRAGMENT = "TWAIN is currently active";

export const AGENT_BUSY_FRAGMENTS = [
  AGENT_BUSY_FRAGMENT,
  "TWAIN est actuellement actif",
  "TWAIN is reeds bezig",
];

// The agent waits up to 60 seconds for the next page. A "no images" reply that took at
// least this long means the agent stopped waiting, not that the scan finished.
export const SLOW_NO_IMAGES_MS = 50000;

export const NO_PAGES_MESSAGE =
  "No pages were received from the scanner. Please try again.";

export function getStoppedWaitingMessage(imagesCaptured) {
  if (!imagesCaptured) {
    return "The scanner did not send any pages in time. Please try again.";
  }
  const pages = imagesCaptured === 1 ? "1 page" : `${imagesCaptured} pages`;
  return `The scanner stopped sending pages after ${pages}. `
    + "Scan the remaining pages again, or append them to the document.";
}

export function getSafeFallbackTwainDevice() {
  return {
    DeviceSourceName: FOLDER_DEVICE_KEY,
    Size: "",
    Resolution: "",
    PixelType: "",
    InputSource: "",
    DuplexType: "",
  };
}

export function isNoImagesMessage(errorMessage) {
  return typeof errorMessage === "string" && NO_IMAGES_MESSAGES.includes(errorMessage.trim());
}

// Classifies an ErrorMessage returned by GetNextImage.
// Returns one of: "complete", "stopped-waiting", "no-pages", "folder-cancelled", "error".
export function classifyNextImageError({ errorMessage, elapsedMs, imagesCaptured, isFolderDevice }) {
  if (!isNoImagesMessage(errorMessage)) {
    return "error";
  }
  if (elapsedMs >= SLOW_NO_IMAGES_MS) {
    return "stopped-waiting";
  }
  if (imagesCaptured > 0) {
    return "complete";
  }
  if (isFolderDevice) {
    return "folder-cancelled";
  }
  return "no-pages";
}

// Only a device the agent itself refused to open should lose its saved selection.
// Transport failures (no agent reply) and the agent's busy reply say nothing about the device.
export function shouldResetSavedDevice(agentErrorMessage) {
  if (typeof agentErrorMessage !== "string" || !agentErrorMessage.trim()) {
    return false;
  }
  return !AGENT_BUSY_FRAGMENTS.some(fragment => agentErrorMessage.includes(fragment));
}

// Picks the device to show when the saved one is missing from the agent's list (never
// chosen, unplugged, or removed). Prefers "Local Files / Network Files" over the first
// listed device: the list is sorted by name, so the first entry is an arbitrary scanner,
// and opening one the user did not choose can hang on its driver.
export function getDefaultDeviceKey(deviceDict) {
  if (!Array.isArray(deviceDict) || !deviceDict.length) {
    return "";
  }
  return deviceDict.some(d => d.Key === FOLDER_DEVICE_KEY)
    ? FOLDER_DEVICE_KEY
    : deviceDict[0].Key;
}
